import { useState, useEffect } from "react";
import { X, Share } from "lucide-react";
import pwaIcons from "../../lib/pwaIcons.json";

const DISMISSED_KEY = "pwa-install-prompt-dismissed";
const VISITED_KEY = "pwa-install-prompt-visited";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandaloneMode(): boolean {
  return (
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

// The home-screen icon itself, taken from the manifest's own list, so the
// prompt shows what will land there.
const appIcon = (
  <img
    src={`${import.meta.env.BASE_URL}${pwaIcons[0].src}`}
    alt=""
    className="flex-shrink-0 w-10 h-10 rounded-soft"
  />
);

function isDismissed(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === "true";
}

export default function PWAInstallPrompt() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosPrompt, setShowIosPrompt] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || isDismissed()) return;

    if (!localStorage.getItem(VISITED_KEY)) {
      localStorage.setItem(VISITED_KEY, "true");
      return;
    }

    if (isIos()) {
      setShowIosPrompt(true);
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setInstallEvent(null);
  };

  if (!visible) return null;

  return (
    <div
      role="banner"
      aria-label="Install app prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm
        bg-warm-bg-card border border-warm-border
        rounded-[12px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-4 animate-banner-slide-up"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="absolute top-3 right-3 text-warm-text-tertiary hover:text-warm-text-secondary transition-colors"
      >
        <X size={16} />
      </button>

      {showIosPrompt ? (
        <div>
          <div className="flex items-center gap-3 pr-6">
            {appIcon}
            <div className="min-w-0">
              <p className="font-heading font-semibold text-sm text-warm-text-primary">
                Install JuDI on your iPhone
              </p>
              <p className="mt-1 text-xs text-warm-text-secondary">
                Add to your home screen for the full app experience.
              </p>
            </div>
          </div>
          <ol className="mt-3 space-y-2">
            <li className="flex items-center gap-2 text-xs text-warm-text-secondary">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-warm-bg-elevated text-warm-blue flex items-center justify-center font-semibold text-[10px]">
                1
              </span>
              Tap the{" "}
              <Share size={13} className="inline text-warm-blue" aria-hidden />{" "}
              <strong>Share</strong> button in Safari
            </li>
            <li className="flex items-center gap-2 text-xs text-warm-text-secondary">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-warm-bg-elevated text-warm-blue flex items-center justify-center font-semibold text-[10px]">
                2
              </span>
              Scroll down and tap <strong>Add to Home Screen</strong>
            </li>
            <li className="flex items-center gap-2 text-xs text-warm-text-secondary">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-warm-bg-elevated text-warm-blue flex items-center justify-center font-semibold text-[10px]">
                3
              </span>
              Tap <strong>Add</strong> to confirm
            </li>
          </ol>
        </div>
      ) : (
        <div className="flex items-center gap-3 pr-6">
          {appIcon}
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-sm text-warm-text-primary">
              Install JuDI
            </p>
            <p className="text-xs text-warm-text-secondary">
              Add to your home screen
            </p>
          </div>
          <button
            onClick={install}
            className="flex-shrink-0 px-3 py-1.5 bg-warm-blue hover:bg-warm-blue-dark text-white text-xs font-semibold rounded-[10px] transition-colors"
          >
            Install
          </button>
        </div>
      )}
    </div>
  );
}
